// Trade-journal accounting + time formatters, ported from the prototype's
// legend/data.js (tradeStats / fmtHold / fmtAgo). Operates on the display-shaped
// round-trips served by /api/symbols/:sym/journal — every derived number the
// JournalCard shows comes from here, computed off the fill ladder (NOT the trade
// doc's exit block, whose exitPrice/exitQty is only the final slice).

export type JournalFill = { t: 'in' | 'out'; time: number; px: number; qty: number; tag: string };

export type JournalTrade = {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  status: string;
  openTime: number;
  closeTime: number | null;
  fills: JournalFill[];
  realizedPnL?: number | null;
  exitReason?: string | null;
};

export type JournalBook = { symbol: string; open: JournalTrade | null; closed: JournalTrade[] };

// Blended-average accounting, verbatim from the prototype: realized P&L prices
// every exit against the whole-trade average entry (not FIFO lots), which is how
// the engine itself books it. `unreal` needs lastPx and only applies to a trade
// still holding shares.
export function tradeStats(tr: JournalTrade, lastPx?: number) {
  const ins = tr.fills.filter((f) => f.t === 'in');
  const outs = tr.fills.filter((f) => f.t === 'out');
  const inSh = ins.reduce((a, f) => a + f.qty, 0);
  const outSh = outs.reduce((a, f) => a + f.qty, 0);
  const avgIn = inSh ? ins.reduce((a, f) => a + f.px * f.qty, 0) / inSh : 0;
  const avgOut = outSh ? outs.reduce((a, f) => a + f.px * f.qty, 0) / outSh : 0;
  const dir = tr.side === 'short' ? -1 : 1;
  // No entry fills but exits present (partial-fill data loss upstream) → pricing
  // exits against a zero basis would book the full proceeds as profit; fall back
  // to the engine's own booked number instead.
  const realized = inSh
    ? outs.reduce((a, f) => a + (f.px - avgIn) * f.qty * dir, 0)
    : (tr.realizedPnL ?? 0);
  const realizedPct = avgIn && outSh ? realized / (avgIn * outSh) : 0;
  const openQty = inSh - outSh;
  const unreal = openQty > 0 && lastPx != null ? (lastPx - avgIn) * openQty * dir : 0;
  const hold = (tr.closeTime || Date.now()) - tr.openTime;
  return { ins, outs, inSh, outSh, avgIn, avgOut, realized, realizedPct, openQty, unreal, hold };
}

// "42m" / "2h 5m" / "3d 4h" — hold-time rounding to the two largest units.
export function fmtHold(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 60) return m + 'm';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h' + (m % 60 ? ' ' + (m % 60) + 'm' : '');
  const d = Math.floor(h / 24);
  return d + 'd' + (h % 24 ? ' ' + (h % 24) + 'h' : '');
}

// "just now" / "5m ago" / "3h ago" / "2d ago" — closed-row recency label.
export function fmtAgo(ts: number | null | undefined): string {
  if (!ts) return '—';
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  return Math.floor(h / 24) + 'd ago';
}
